/* eslint-disable jira/filename-case */

import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';
import type { ProjectSidebarNavInfo } from '@jira/navigation-apps__resources/src/services/sidebar/project-sidebar-navigation/types.tsx';
import { extractData as extractProjectBoardData } from '@jira/navigation-apps__sidebar-common/src/controllers/board/utils/extract-data.tsx';
import { extractData as extractProjectData } from '@jira/navigation-apps__sidebar-utils/src/utils/extract-data/index.tsx';
import type { ContainerContext } from '@jira/platform__services-providers-container-context/src/types.tsx';
import type { AppDescriptor } from '../types.tsx';
import type { TransformedProductContext } from './types.tsx';

// App descriptor shape cames from atlassian-connect/jsapi-v5/src/main/resources/v5/js/iframe/combined/iframe-insertion.js
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const transformResponse = (response: any): AppDescriptor => {
	try {
		const productContext = response.productCtx ? JSON.parse(response.productCtx) : {};
		let structuredContext = response.structuredContext
			? JSON.parse(response.structuredContext)
			: {};

		structuredContext = {
			...structuredContext,
			jira: {
				...structuredContext.jira,
				horizontalNavigation: true,
			},
		};

		const context = {
			productContext,
			structuredContext,
			productCtx: productContext,
		};

		return {
			...response,
			...context,
			isFullPage: response.general === '1',
			user: {
				timeZone: response.timeZone,
				uid: response.uid || undefined,
				ukey: response.ukey || undefined,
				fullName: response.fullName || undefined,
			},
			targets: {
				env: {
					resize: 'both',
				},
			},
			history: {
				state: '',
			},
			autoresize: true,
			ns: response.uniqueKey,
			containerId: `embedded-${response.uniqueKey}`,
			contextPath: response.cp,
			width: response.w || response.width,
			height: response.h || response.height,
			type: 'MODULE',
		};
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const location = 'connect-iframe.transformer';
		log.safeErrorWithoutCustomerData(location, 'Failed to transform response', error);

		return {
			autoresize: true,
			productCtx: undefined,
			structuredContext: undefined,
			isFullPage: true,
			history: {
				state: '',
			},
			targets: {
				env: {
					resize: 'both',
				},
			},
			type: 'MODULE',
		};
	}
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const checkValueAndMapToNewName = (object: any, oldName: string, newName: string): any | null => {
	const newValue = object !== null && object[oldName] ? String(object[oldName]) : null;
	return newValue !== null ? { [newName]: newValue } : null;
};

export const transformContext = (
	contextData: ContainerContext | null,
): TransformedProductContext => {
	const scope = (contextData && contextData.scope) || null;
	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	const project = (contextData && contextData.project) || null;

	const projectId = checkValueAndMapToNewName(project, 'id', 'project.id');
	const projectKey = checkValueAndMapToNewName(project, 'key', 'project.key');
	const boardType = checkValueAndMapToNewName(scope, 'type', 'board.type');
	const boardId = checkValueAndMapToNewName(scope, 'id', 'board.id');

	return {
		...projectId,
		...projectKey,
		...boardType,
		...boardId,
	};
};
export const extractContext = (
	projectSidebarNavInfo: ProjectSidebarNavInfo | null,
): TransformedProductContext => {
	const project = (projectSidebarNavInfo && extractProjectData(projectSidebarNavInfo)) || null;
	const board =
		(projectSidebarNavInfo?.jiraSoftwareNavData &&
			extractProjectBoardData(projectSidebarNavInfo.jiraSoftwareNavData)) ||
		null;

	const projectId = checkValueAndMapToNewName(project, 'id', 'project.id');
	const projectKey = checkValueAndMapToNewName(project, 'key', 'project.key');
	const boardType = checkValueAndMapToNewName(board, 'type', 'board.type');
	const boardId = checkValueAndMapToNewName(board, 'id', 'board.id');

	return {
		...projectId,
		...projectKey,
		...boardType,
		...boardId,
	};
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const transformQueryParams = (contextData: ContainerContext | null, query: any): any => {
	const context = transformContext(contextData);
	return {
		...query,
		...context,
	};
};

export const transformUrlToAddonAndModuleKeys = (
	query: RouterContext['query'],
): {
	addonKey: string;
	moduleKey: string;
} => {
	const { selectedItem } = query;
	let addonKey = '';
	let moduleKey = '';

	if (selectedItem) {
		const urlPattern = selectedItem.includes('%3A')
			? 'com.atlassian.plugins.atlassian-connect-plugin%3A'
			: 'com.atlassian.plugins.atlassian-connect-plugin:';

		[addonKey, moduleKey] = selectedItem.replace(urlPattern, '').split('__');
	}
	return { addonKey, moduleKey };
};

export const buildNotFoundUrl = (containerContext: ContainerContext | null): string | null => {
	if (!containerContext) {
		return null;
	}
	if (containerContext.contextType !== 'project') {
		return null;
	}

	const project = containerContext.project;
	const scope = containerContext.scope;

	const projectKey = project.key;
	const projectType = project.type;
	const scopeId = scope?.id;

	if (projectType === 'business') {
		return `/jira/core/projects/${projectKey}/not-found`;
	}
	if (projectType === 'software' && scopeId) {
		return `/jira/software/projects/${projectKey}/boards/${scopeId}/not-found`;
	}

	return null;
};

export const transformContextToFromUrl = (
	match: RouterContext['match'],
	query: RouterContext['query'],
	hash: string,
): string => {
	const queryString = new URLSearchParams(query);
	const originalUrl = new URL(`${globalThis.location.origin}${match.url}`);
	queryString.forEach((value, key) => {
		originalUrl.searchParams.append(key, value);
	});
	originalUrl.hash = hash;
	return originalUrl.href;
};
